import React, { memo } from 'react'
import { Aircraft, Transaction, Event, Company } from '../../types/intellijetnet'
import { AircraftOverride, AircraftTransaction, Option, TransactionOverride, MarketCustomColumn, AircraftChangeInfo, MarketEMP, TakePriceModifier } from '../../types/database'
import { getAircraftHighlightClass, isUnderContract } from '../../utils/change-detection'
import { getLiveViewRowFillClass, type TableType } from '../../reports/marketSummary/formatting'
import type { EditingCell, EditingCellFieldName, CombinedAircraftData, ColumnVisibility } from './types'
import type { EffectiveAircraft, EffectiveTransactionAircraft } from '../../hooks/useMarketTableData'
import type { RelativeValueBreakdown } from '../../hooks/useRelativeValue'
import {
  SerialNumberCell, CompanyCell, YearCell, AFTTCell, EMPCell,
  DamageCell, PAXCell, PaintInteriorCell, CustomColumnsCells, OptionsCell,
  ForSalePricingCells, SoldPricingCells, RemovedPricingCells, RelativeValueCell,
} from './cells'

export interface MarketTableRowProps {
  combinedItem: CombinedAircraftData
  tableType: 'for-sale' | 'sold' | 'removed'
  isEditMode?: boolean
  cellBorderClass: string
  columnVisibility: ColumnVisibility

  // Data
  overrides: Map<number, AircraftOverride>
  transactionOverrides: Map<string, TransactionOverride>
  companyMap: Map<number, Company>
  changeInfo: Map<number, AircraftChangeInfo>
  customColumns: MarketCustomColumn[]
  marketEmps: MarketEMP[]
  options: Map<number, Option[]>
  transactionOptions: Map<string, Option>
  relativeValues: Map<number, number | null>
  relativeValueBreakdowns: Map<number, RelativeValueBreakdown>
  missingABB: Set<number>

  // State
  editingCell: EditingCell | null
  editingOptionsAircraftId: number | null
  setEditingOptionsAircraftId: (id: number | null) => void

  // Getters
  getEffectiveAircraft: (aircraft: Aircraft) => EffectiveAircraft
  getEffectiveTransactionAircraft: (aircraft: Aircraft, aircraftTransaction: AircraftTransaction | null | undefined) => EffectiveTransactionAircraft
  getEffectiveTransactionData: (aircraftTransaction: AircraftTransaction | null | undefined, transaction: Transaction | null | undefined) => { asking_price: any; sale_price: any }

  // Navigation
  handleRowClick: (item: Aircraft | { aircraft: Aircraft; transaction?: Transaction | null; event?: Event | null; aircraftTransaction?: AircraftTransaction | null }, isOffMarket?: boolean, isTemporary?: boolean) => void

  // Editing handlers
  handleTakePriceSave: (aircraftId: number, priceFieldName: 'take_price' | 'external_take_price', newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
  handleAskPriceSave: (aircraftId: number, priceFieldName: 'asking_price' | 'external_asking_price', newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
  handleFieldSave: (aircraftId: number, fieldName: EditingCellFieldName, newValue: string | number | null) => Promise<void>
  handleCompanySave: (aircraftId: number, fieldType: 'owner' | 'broker', companyId: number | null, customValue: string | null) => Promise<void>
  handleTransactionPriceSave: (transactionId: string, fieldName: EditingCellFieldName, newValue: number | null) => Promise<void>
  handleTransactionTakePriceSave: (transactionId: string, priceFieldName: 'take_price' | 'external_take_price', newValue: number | null, newModifier: TakePriceModifier | null) => Promise<void>
  handleTransactionAskPriceSave: (transactionId: string, priceFieldName: 'asking_price' | 'external_asking_price', newValue: number | null, newMakeOffer: boolean | null) => Promise<void>
  handleTransactionFieldSave: (transactionId: string, fieldName: EditingCellFieldName, newValue: string | number | null) => Promise<void>
  handleTransactionCompanySave: (transactionId: string, fieldType: 'owner' | 'broker', companyId: number | null, customValue: string | null) => Promise<void>
  handleStartEdit: (aircraftId: number, fieldName: EditingCellFieldName) => void
  handleStartTransactionEdit: (aircraftId: number, transactionId: string, fieldName: EditingCellFieldName) => void
  handleCancelEdit: () => void
  handleOptionsSave: (aircraftId: number, newOptions: string[]) => Promise<void>
  handleCancelOptionsEdit: () => void
  handleTransactionOptionsSave: (transactionId: string, newOptions: string[]) => Promise<void>
}

export const MarketTableRow = memo(function MarketTableRow(props: MarketTableRowProps) {
  const {
    combinedItem,
    tableType,
    isEditMode,
    cellBorderClass,
    columnVisibility: cv,
    overrides,
    transactionOverrides,
    companyMap,
    changeInfo,
    customColumns,
    marketEmps,
    options,
    transactionOptions,
    relativeValues,
    relativeValueBreakdowns,
    missingABB,
    editingCell,
    editingOptionsAircraftId,
    setEditingOptionsAircraftId,
    getEffectiveAircraft,
    getEffectiveTransactionAircraft,
    getEffectiveTransactionData,
    handleRowClick,
    handleTakePriceSave,
    handleAskPriceSave,
    handleFieldSave,
    handleCompanySave,
    handleTransactionPriceSave,
    handleTransactionTakePriceSave,
    handleTransactionAskPriceSave,
    handleTransactionFieldSave,
    handleTransactionCompanySave,
    handleStartEdit,
    handleStartTransactionEdit,
    handleCancelEdit,
    handleOptionsSave,
    handleCancelOptionsEdit,
    handleTransactionOptionsSave,
  } = props

  // Extract column visibility flags
  const hideSerialNumber = cv.hideSerialNumber ?? false
  const hideYearMfr = cv.hideYearMfr ?? false
  const hideAftt = cv.hideAftt ?? false
  const hideOwner = cv.hideOwner ?? false
  const hideBroker = cv.hideBroker ?? false
  const hideEmp = cv.hideEmp ?? false
  const hideDmg = cv.hideDmg ?? false
  const hidePax = cv.hidePax ?? false
  const hidePaintInterior = cv.hidePaintInterior ?? false
  const hideOptions = cv.hideOptions ?? false
  const hideDateListed = cv.hideDateListed ?? false
  const hideJnAsk = cv.hideJnAsk ?? false
  const hideAskingPrice = cv.hideAskingPrice ?? false
  const hideExternalColumns = cv.hideExternalColumns ?? false
  const hideRelativeValue = cv.hideRelativeValue ?? false

  // Destructure combined item
  const { aircraft, source, transaction, event, offMarket, aircraftTransaction } = combinedItem

  // Get effective aircraft data based on source
  let effectiveAircraft: Aircraft & { take_price?: number | null; emp?: string | null; tbo?: number | null; pax?: number | null; paint_date?: number | null; interior_date?: number | null }
  if (tableType === 'sold') {
    effectiveAircraft = getEffectiveTransactionAircraft(aircraft, aircraftTransaction)
  } else {
    effectiveAircraft = getEffectiveAircraft(aircraft)
  }

  // Check if aircraft is off-market based on status override (only for For Sale container)
  const isStatusOffMarket = tableType === 'for-sale' && effectiveAircraft.status === 'Off Market'
  const isOffMarket = source === 'offMarket' || isStatusOffMarket
  const isTemporary = source === 'temporary'

  // Check deal pending status for row fill
  const isDealPending = tableType === 'for-sale' && isUnderContract(aircraft, overrides.get(aircraft.aircraft_id))

  // Get change info for highlighting (only for API aircraft, not off-market, temporary, or sold container)
  const aircraftChangeInfo = (isOffMarket || isTemporary || tableType === 'sold') ? null : changeInfo.get(aircraft.aircraft_id)
  const highlightClass = aircraftChangeInfo ? getAircraftHighlightClass(aircraftChangeInfo) : ''

  // Determine row background class using shared formatting utility
  let rowBgClass: string
  if (tableType === 'sold' || tableType === 'removed') {
    rowBgClass = getLiveViewRowFillClass({
      tableType: tableType as TableType,
      isOffMarket: false,
      isDealPending: false
    })
  } else if (isOffMarket || isDealPending) {
    rowBgClass = getLiveViewRowFillClass({
      tableType: 'for-sale',
      isOffMarket,
      isDealPending
    })
  } else if (highlightClass) {
    const hoverClass = highlightClass.replace('bg-', 'hover:bg-').replace('-100', '-50')
    rowBgClass = `${highlightClass} ${hoverClass}`
  } else {
    rowBgClass = 'hover:bg-gray-50'
  }

  // Generate unique row ID for scroll restoration
  const rowId = tableType === 'for-sale'
    ? `${aircraft.aircraft_id}`
    : tableType === 'sold'
      ? `sold-${aircraft.aircraft_id}-${aircraftTransaction?.transaction_id || transaction?.trans_id || 'static'}`
      : `removed-${aircraft.aircraft_id}-${event?.event_id || 'static'}`

  // Handler for navigating to aircraft details
  const handleNavigate = () => {
    if (tableType === 'for-sale') {
      handleRowClick(aircraft, isOffMarket, isTemporary)
    } else if (tableType === 'sold') {
      handleRowClick({ aircraft, transaction, aircraftTransaction }, isOffMarket, isTemporary)
    } else {
      handleRowClick({ aircraft, event }, isOffMarket, isTemporary)
    }
  }

  // Shared props for all cell components that use CellBaseProps
  const cellBaseProps = {
    aircraft,
    tableType,
    isEditMode,
    cellBorderClass,
    effectiveAircraft,
    overrides,
    transactionOverrides,
    aircraftTransaction,
    editingCell,
    handleStartEdit,
    handleStartTransactionEdit,
    handleCancelEdit,
    handleFieldSave,
    handleTransactionFieldSave,
  }

  return (
    <tr
      key={`${source}-${aircraft.aircraft_id}-${aircraftTransaction?.transaction_id || transaction?.trans_id || event?.event_id || 'static'}`}
      data-aircraft-id={aircraft.aircraft_id}
      data-row-id={rowId}
      className={`${isEditMode ? '' : 'cursor-pointer'} ${rowBgClass}`}
      onClick={isEditMode ? undefined : handleNavigate}
    >
      {/* S/N column */}
      {!hideSerialNumber && (
        <SerialNumberCell
          aircraft={aircraft}
          cellBorderClass={cellBorderClass}
          isEditMode={isEditMode}
          isOffMarket={isOffMarket}
          isTemporary={isTemporary}
          handleNavigate={handleNavigate}
        />
      )}

      {/* Owner column */}
      {!hideOwner && (
        <CompanyCell
          {...cellBaseProps}
          fieldType="owner"
          companyMap={companyMap}
          handleCompanySave={handleCompanySave}
          handleTransactionCompanySave={handleTransactionCompanySave}
        />
      )}

      {/* Broker column */}
      {!hideBroker && (
        <CompanyCell
          {...cellBaseProps}
          fieldType="broker"
          companyMap={companyMap}
          handleCompanySave={handleCompanySave}
          handleTransactionCompanySave={handleTransactionCompanySave}
        />
      )}

      {/* Year/EIS column */}
      {!hideYearMfr && <YearCell {...cellBaseProps} />}

      {/* AFTT column */}
      {!hideAftt && <AFTTCell {...cellBaseProps} />}

      {/* EMP column */}
      {!hideEmp && <EMPCell {...cellBaseProps} marketEmps={marketEmps} />}

      {/* Damage column */}
      {!hideDmg && <DamageCell {...cellBaseProps} />}

      {/* PAX column */}
      {!hidePax && <PAXCell {...cellBaseProps} />}

      {/* Paint/Interior column */}
      {!hidePaintInterior && <PaintInteriorCell {...cellBaseProps} />}

      {/* Custom Columns */}
      <CustomColumnsCells {...cellBaseProps} customColumns={customColumns} />

      {/* Options column */}
      {!hideOptions && (
        <OptionsCell
          {...cellBaseProps}
          options={options}
          transactionOptions={transactionOptions}
          editingOptionsAircraftId={editingOptionsAircraftId}
          setEditingOptionsAircraftId={setEditingOptionsAircraftId}
          handleOptionsSave={handleOptionsSave}
          handleCancelOptionsEdit={handleCancelOptionsEdit}
          handleTransactionOptionsSave={handleTransactionOptionsSave}
        />
      )}

      {/* For-sale specific pricing columns */}
      {tableType === 'for-sale' && (
        <ForSalePricingCells
          aircraft={aircraft}
          effectiveAircraft={effectiveAircraft as EffectiveAircraft}
          isEditMode={isEditMode}
          cellBorderClass={cellBorderClass}
          isOffMarket={isOffMarket}
          overrides={overrides}
          editingCell={editingCell}
          hideDateListed={hideDateListed}
          hideJnAsk={hideJnAsk}
          hideAskingPrice={hideAskingPrice}
          hideExternalColumns={hideExternalColumns}
          handleStartEdit={handleStartEdit}
          handleCancelEdit={handleCancelEdit}
          handleFieldSave={handleFieldSave}
          handleTakePriceSave={handleTakePriceSave}
          handleAskPriceSave={handleAskPriceSave}
        />
      )}

      {/* Sold specific pricing columns */}
      {tableType === 'sold' && (
        <SoldPricingCells
          aircraft={aircraft}
          effectiveAircraft={effectiveAircraft as EffectiveTransactionAircraft}
          isEditMode={isEditMode}
          cellBorderClass={cellBorderClass}
          overrides={overrides}
          transactionOverrides={transactionOverrides}
          editingCell={editingCell}
          transaction={transaction}
          aircraftTransaction={aircraftTransaction}
          hideAskingPrice={hideAskingPrice}
          hideExternalColumns={hideExternalColumns}
          getEffectiveTransactionData={getEffectiveTransactionData}
          handleStartTransactionEdit={handleStartTransactionEdit}
          handleCancelEdit={handleCancelEdit}
          handleTransactionPriceSave={handleTransactionPriceSave}
          handleTransactionTakePriceSave={handleTransactionTakePriceSave}
          handleTransactionAskPriceSave={handleTransactionAskPriceSave}
        />
      )}

      {/* Removed specific pricing columns */}
      {tableType === 'removed' && (
        <RemovedPricingCells
          aircraft={aircraft}
          effectiveAircraft={effectiveAircraft}
          isEditMode={isEditMode}
          cellBorderClass={cellBorderClass}
          isOffMarket={isOffMarket}
          overrides={overrides}
          editingCell={editingCell}
          event={event}
          offMarket={offMarket}
          hideExternalColumns={hideExternalColumns}
          handleStartEdit={handleStartEdit}
          handleCancelEdit={handleCancelEdit}
          handleAskPriceSave={handleAskPriceSave}
          handleTakePriceSave={handleTakePriceSave}
        />
      )}

      {/* Relative Value column - always last */}
      {!hideRelativeValue && (
        <RelativeValueCell
          aircraftId={aircraft.aircraft_id}
          cellBorderClass={cellBorderClass}
          relativeValues={relativeValues}
          relativeValueBreakdowns={relativeValueBreakdowns}
          missingABB={missingABB}
        />
      )}
    </tr>
  )
})

